import type { AudioImportFile, ZabitBridge } from '../../renderer/src/global';
import { CHANNELS } from '../../src/ipc/channels';
import { chat, chatSend, summarizeTranscript } from '../../src/services/ai';
import { deleteAllChatThreads, deleteChatThread, getChatThread, listChatThreads, renameChatThread } from '../../src/services/chatThreads';
import { deleteMyAccount } from '../../src/services/account';
import { apiRequest } from '../../src/services/api';
import { cancelBillingSubscription, getBillingStatus, redeemCoupon, refreshBillingSubscription, requestCheckoutUrl } from '../../src/services/billing';
import { cancelBotForEvent, sendBot, startBotSync, stopMeetingBot, triggerBotSync } from '../../src/services/botSync';
import { submitFeatureRequest } from '../../src/services/featureRequests';
import { submitAiReport } from '../../src/services/aiReports';
import { t } from '../../src/services/language';
import { startLiveSessionSignals } from '../../src/services/liveSessions';
import { getSummaryEmailStatus, sendSummaryEmail } from '../../src/services/mailer';
import { getMeetingMedia } from '../../src/services/meetingMedia';
import { acceptLegal, completeSetup, getLegalLinks, getOnboardingState } from '../../src/services/onboarding';
import { startPrefsSync } from '../../src/services/prefsSync';
import { setRecordingActive } from '../../src/services/recordingState';
import {
  getAppLanguage,
  getAppPrefs,
  getSettings,
  isTranscribeLanguage,
  saveAppLanguage,
  saveAppPrefs,
  saveEmailSettings,
  saveSettings,
  type AppPrefs,
  type AutoJoinPrefs,
  type DiagnosticsInfo,
} from '../../src/services/settings';
import { SHORTCUTS } from '../../src/services/shortcuts';
import { assertAdmin, getAuthState } from '../../src/services/supabase/auth';
import {
  createCompany,
  deletePerson,
  getCompanyProfile,
  getPersonProfile,
  listCompanies,
  listPeople,
  mergePeople,
  updateCompany,
  updatePerson,
} from '../../src/services/supabase/directoryRepo';
import {
  createMeeting,
  deleteMeetings,
  listMeetings,
  listScheduledEventIds,
  setSpeakerPerson,
  updateMeeting,
} from '../../src/services/supabase/meetingsRepo';
import { getPlanUsage } from '../../src/services/supabase/planRepo';
import { changePassword, getProfile, updateProfile } from '../../src/services/supabase/profileRepo';
import { listTrash, purgeMeetings, restoreMeetings } from '../../src/services/supabase/trashRepo';
import { getSupportAccess, setSupportAccess } from '../../src/services/supportAccess';
import { shareClient } from '../../src/services/meetingShare';
import { createFolder, deleteFolder, listFolders, moveMeetingToFolder, renameFolder } from '../../src/services/supabase/foldersRepo';
import type { AppPrefsDTO, BotSendRequest, LiveSource, MeetingDTO, UpdateStatusDTO } from '../../src/shared/types';
import { subscribe } from './bus';
import { disconnectCalendar, forgetCalendarAccounts, takeCalendarConnectReturn } from './calendarAccounts';
import { getSupabase } from './shims/supabaseClient';
import { WEB_CONFIG } from './config';
import { deleteChunks, listChunkRecordings, putChunk, readChunks } from './idb';
import { exportArchive } from './shims/archiveExport';
import { discardAudioFile, pickAudioFile, transcribeAudioFile } from './shims/audioImport';
import { closeLiveSource, openLiveSource, pushLiveAudio } from './shims/deepgramLive';
import { saveFeedback } from './shims/feedback';
import { connectGoogle } from './shims/googleAuth';
import {
  getCalendarStatus,
  getTodayCalendarEvents,
  getUpcomingCalendarEvents,
  listGoogleCalendars,
  setGoogleCalendars,
} from './shims/googleCalendar';
import { archiveRecordedMeeting, notifyMeetingsChanged } from './shims/meetingArchive';
import { pageWebContents } from './shims/mainWindow';
import { connectMicrosoft, getOutlookStatus } from './shims/microsoftAuth';
import { openExternalSafely } from './shims/policies';
import { localAudioUrl, primeLocalAudioUrls, revealRecording, saveRecording, storeBlob } from './shims/recordings';
import { contactSupport } from './shims/support';
import { appleSignIn } from './appleSignIn';
import { nativeMic } from './nativeMic';
import { sharedAudio } from './sharedAudio';
import { beforeSignOut, pushNotifications } from './pushNotifications';
import { storeBilling } from './storeBilling';
import { requestPasswordReset, resendSignupCode, resetPassword, signInWithEmail, signUpWithEmail, verifySignupCode } from './emailAuth';
import { awaitSignInReturn, finishSignInRedirect, forwardGoogleCalendarGrant, hydratePreferences, signInWithGoogleRedirect, takeSignInError } from './webAuth';

/**
 * `window.zabit`'in tarayıcı sürümü — masaüstündeki preload.ts + ipc/register.ts
 * ikilisinin karşılığı. Arayüz (renderer/) iki ortamda da aynı köprüyü görüyor;
 * burada her metot masaüstündeki IPC ucunun çağırdığı servise gidiyor.
 * Masaüstüne özgü olanlar (pencere, kısayollar, güncelleme, görüşme algılama,
 * yüzen uyarı penceresi) boş.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function meetingIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && UUID.test(id)).slice(0, 100);
}

function requireId(value: unknown): string {
  if (typeof value !== 'string' || !value) throw new Error(t('Geçersiz kimlik.', 'Invalid id.'));
  return value;
}

function isLiveSource(value: unknown): value is LiveSource {
  return value === 'mic' || value === 'system';
}

const noop = () => {};
const unsubscribeNoop = () => noop;

function withDesktopExtras(prefs: AppPrefs): AppPrefsDTO {
  return { ...prefs, failedShortcuts: [], shortcuts: SHORTCUTS, callDetectionSupported: false };
}

// ————————————————————————————————————————————————
// Oturum açıldıktan sonraki işler (sayfa başına bir kez)
// ————————————————————————————————————————————————

let startedForUser: string | null = null;
let localFilesReady: Promise<void> | null = null;

function afterSignIn(userId: string): void {
  if (startedForUser === userId) return;
  startedForUser = userId;
  void (async () => {
    // Önce sunucudaki tercihler: yeni tarayıcının varsayılanları onları ezmesin.
    await hydratePreferences(userId).catch((err) => console.warn('[bridge] tercihler okunamadı:', err));
    startPrefsSync();
    startLiveSessionSignals();
    // Bot durumu, sunucunun bıraktığı işler, yarıda kalan arşivler (30 sn'de bir).
    startBotSync();
    await forwardGoogleCalendarGrant();
  })();
}

// ————————————————————————————————————————————————
// Yarıda kalan kayıtlar
// ————————————————————————————————————————————————

/** Şu an sürmekte olan kaydın kimliği — kurtarma onu yarım sanmasın. */
let activeRecordingId: string | null = null;
let recoveryTaken = false;

/** Telefonun dosyası bu kadar içindeyse tarayıcıdaki parçalar aynı kaydın (boşluklu) kopyası sayılır. */
const SAME_RECORDING_SLACK_MS = 60_000;

async function recoverRecordings(): Promise<AudioImportFile[]> {
  const recovered: AudioImportFile[] = [];
  const recoveredName = (startedAt: number, ext: string) =>
    t(`Kurtarılan kayıt ${new Date(startedAt).toLocaleString('tr-TR')}.${ext}`, `Recovered recording ${new Date(startedAt).toLocaleString('en-US')}.${ext}`);

  // Mobil: telefonun kendi dosyası kesintisiz; tarayıcının parçaları arka planda kısılıp
  // boşluklu kalmış olabilir. Önce telefonun dosyaları, aynı kaydın parçaları atlanır.
  const nativeSpans: Array<{ from: number; to: number }> = [];
  for (const orphan of (await nativeMic?.takeOrphans().catch(() => [])) ?? []) {
    const fileName = recoveredName(orphan.startedAtMs, 'aac');
    try {
      const filePath = await storeBlob(orphan.audio, fileName, 'recording');
      await nativeMic?.releaseOrphan(orphan.path);
      nativeSpans.push({ from: orphan.startedAtMs - SAME_RECORDING_SLACK_MS, to: orphan.endedAtMs + SAME_RECORDING_SLACK_MS });
      recovered.push({
        filePath,
        fileName,
        durationSeconds: Math.round(orphan.durationMs / 1000),
        recordedAtISO: new Date(orphan.startedAtMs).toISOString(),
        liveWindow: { fromISO: new Date(orphan.startedAtMs).toISOString(), toISO: new Date(orphan.endedAtMs).toISOString() },
      });
    } catch (err) {
      console.error('[bridge] telefondaki yarım kayıt kurtarılamadı:', err);
    }
  }

  for (const recordingId of await listChunkRecordings()) {
    if (recordingId === activeRecordingId) continue;
    const chunks = await readChunks(recordingId);
    if (chunks.length === 0) continue;
    const startedAt = chunks[0].at;
    // Parçalar ~10 sn: son parçanın zamanı + 10 sn kaydın sonu.
    const endedAt = chunks[chunks.length - 1].at + 10_000;
    if (nativeSpans.some((span) => startedAt >= span.from && startedAt <= span.to)) {
      await deleteChunks(recordingId).catch(() => {});
      continue;
    }
    const blob = new Blob(
      chunks.map((chunk) => chunk.blob),
      { type: 'audio/webm' }
    );
    const fileName = recoveredName(startedAt, 'webm');
    try {
      const filePath = await storeBlob(blob, fileName, 'recording');
      await deleteChunks(recordingId);
      recovered.push({
        filePath,
        fileName,
        // Süre sunucuda dökümden ölçülüyor.
        durationSeconds: Math.round((endedAt - startedAt) / 1000),
        recordedAtISO: new Date(startedAt).toISOString(),
        liveWindow: { fromISO: new Date(startedAt).toISOString(), toISO: new Date(endedAt).toISOString() },
      });
    } catch (err) {
      console.error('[bridge] yarım kayıt kurtarılamadı:', recordingId, err);
    }
  }
  return recovered;
}

function updateStatus(): UpdateStatusDTO {
  return { state: 'unsupported', currentVersion: WEB_CONFIG.version };
}

async function autoJoinRequest(body?: Record<string, unknown>): Promise<AutoJoinPrefs> {
  const prefs = body
    ? await apiRequest<Partial<AutoJoinPrefs>>('POST', '/api/v1/calendar/auto-join', { body })
    : await apiRequest<Partial<AutoJoinPrefs>>('GET', '/api/v1/calendar/auto-join');
  return {
    autoJoinBot: prefs.autoJoinBot === true,
    autoJoinExcluded: prefs.autoJoinExcluded ?? [],
    autoJoinIncluded: prefs.autoJoinIncluded ?? [],
  };
}

export const webBridge: ZabitBridge = {
  // — Kayıt —
  getDesktopSources: async () => [],
  saveRecording: async (buffer, recordingId, mimeType) => {
    const filePath = await saveRecording(buffer, mimeType);
    if (typeof recordingId === 'string') {
      if (activeRecordingId === recordingId) activeRecordingId = null;
      await deleteChunks(recordingId).catch(() => {});
    }
    return filePath;
  },
  revealRecording: (filePath) => revealRecording(filePath),

  // — Ses dosyası yükleme —
  pickAudioFile: () => pickAudioFile(),
  transcribeAudioFile: (filePath, options) => transcribeAudioFile(filePath, options),
  discardAudioFile: (filePath) => discardAudioFile(filePath),

  // — Dışarıdan içe aktarma — Granola API'si tarayıcıdan çağrılamıyor (CORS); arayüzde gizli.
  importFromGranola: async () => {
    throw new Error(t('Granola içe aktarımı yalnızca masaüstü uygulamasında.', 'Granola import is only available in the desktop app.'));
  },
  onImportProgress: unsubscribeNoop,

  // — Canlı transkript —
  liveOpen: async (sessionId, source, language) => {
    if (typeof sessionId !== 'string' || !isLiveSource(source)) {
      throw new Error(t('Geçersiz canlı transkript isteği.', 'Invalid live transcript request.'));
    }
    return openLiveSource(pageWebContents(), sessionId, source, isTranscribeLanguage(language) ? language : 'tr');
  },
  liveAudio: (sessionId, source, data) => {
    if (typeof sessionId !== 'string' || !isLiveSource(source) || !(data instanceof ArrayBuffer)) return;
    pushLiveAudio(sessionId, source, data);
  },
  liveClose: async (sessionId, source) => {
    if (typeof sessionId !== 'string' || !isLiveSource(source)) return;
    return closeLiveSource(sessionId, source);
  },
  onLiveEvent: (callback) => subscribe(CHANNELS.liveEvent, callback),

  // — Yapay zekâ —
  chatWithAI: (history, liveTranscript) => chat(history, liveTranscript),
  chatSend: (threadId, text) => chatSend(threadId, text),
  listChatThreads: () => listChatThreads(),
  getChatThread: (id) => getChatThread(id),
  renameChatThread: (id, title) => renameChatThread(id, title),
  deleteChatThread: (id) => deleteChatThread(id),
  deleteAllChatThreads: () => deleteAllChatThreads(),
  summarizeMeeting: (transcript, title) => summarizeTranscript(transcript ?? [], title ?? t('Toplantı', 'Meeting')),

  // — Kimlik doğrulama —
  signIn: () => signInWithGoogleRedirect(),
  signOut: async () => {
    // Mobil: bu telefonun bildirim kaydı silinsin (oturum kapanmadan, istek atılabilirken).
    await beforeSignOut().catch(() => {});
    // Yalnızca bu tarayıcının oturumu: Supabase'in varsayılanı (global) masaüstündeki
    // oturumu da kapatırdı. Takvim bağlantısı sunucuda kalır (otomatik bot sürsün).
    await getSupabase().auth.signOut({ scope: 'local' }).catch(() => {});
    forgetCalendarAccounts();
    startedForUser = null;
  },
  deleteAccount: (confirmEmail) => deleteMyAccount(confirmEmail),
  getAuthState: async () => {
    await awaitSignInReturn();
    const state = await getAuthState();
    finishSignInRedirect();
    if (state.signedIn && state.user) afterSignIn(state.user.id);
    return state;
  },

  // — İlk kurulum ve yasal onaylar —
  getOnboardingState: () => getOnboardingState(),
  acceptLegal: (documents) => acceptLegal(documents),
  completeOnboarding: () => completeSetup(),
  getLegalLinks: async () => getLegalLinks(),
  openMicPrivacySettings: async () => false,
  onConsentRequired: (callback) => subscribe(CHANNELS.consentRequired, callback),
  onUpdateRequired: (callback) => subscribe(CHANNELS.updateRequired, (minVersion: string | null) => callback(minVersion ?? null)),

  // — Profil ve arayüz dili —
  getProfile: () => getProfile(),
  updateProfile: (patch) => updateProfile(patch),
  changePassword: (input) => changePassword(input),
  getPlanUsage: () => getPlanUsage(),
  getAppLanguage: () => getAppLanguage(),
  setAppLanguage: (language) => saveAppLanguage(language),

  // — Kişisel tercihler —
  getAppPrefs: async () => withDesktopExtras(await getAppPrefs()),
  saveAppPrefs: async (patch) => withDesktopExtras(await saveAppPrefs(patch)),
  onShortcutAction: unsubscribeNoop,
  setRecordingWindowMode: (active) => setRecordingActive(active === true),

  // — Yüzen uyarı penceresi (masaüstüne özgü) —
  getMeetingPrompt: async () => null,
  onMeetingPrompt: unsubscribeNoop,
  meetingPromptAction: noop,
  onMeetingPromptRecord: unsubscribeNoop,
  onCallState: unsubscribeNoop,

  // — Kalıcı depolama —
  listMeetings: async () => {
    // Arşivlenmemiş kayıtların oynatma adresleri toplantılar eşlenmeden önce hazır olsun.
    await (localFilesReady ??= primeLocalAudioUrls());
    return listMeetings();
  },
  createMeeting: async (meeting) => {
    const created = await createMeeting(meeting);
    if (created.audioFilePath) {
      void archiveRecordedMeeting({
        meetingId: created.id,
        audioPath: created.audioFilePath,
        transcript: created.transcript,
        summary: {
          ...created.summary,
          actionItems: created.actionItems.map((item) => ({ text: item.text, assignee: item.assignee })),
        },
      });
    }
    return created;
  },
  updateMeeting: async (meeting: MeetingDTO) => {
    if ((await updateMeeting(meeting)).stale) notifyMeetingsChanged();
  },
  deleteMeetings: (ids) => deleteMeetings(ids ?? []),
  listTrash: () => listTrash(),
  restoreMeetings: async (ids) => {
    await restoreMeetings(meetingIds(ids));
    notifyMeetingsChanged();
    triggerBotSync();
  },
  purgeMeetings: (ids) => purgeMeetings(meetingIds(ids)),
  getMeetingMedia: (meetingId) => getMeetingMedia(meetingId),
  getSupportAccess: (meetingId) => getSupportAccess(requireId(meetingId)),
  setSupportAccess: (meetingId, enabled) => setSupportAccess(requireId(meetingId), enabled === true),
  meetingShare: shareClient('meeting'),
  folders: {
    list: () => listFolders(),
    create: (name) => createFolder(name),
    rename: (id, name) => renameFolder(requireId(id), name),
    remove: async (id) => {
      await deleteFolder(requireId(id));
      notifyMeetingsChanged();
    },
    moveMeeting: async (meetingId, folderId) => {
      await moveMeetingToFolder(requireId(meetingId), folderId === null ? null : requireId(folderId));
      notifyMeetingsChanged();
    },
    share: shareClient('folder'),
  },
  onMeetingsChanged: (callback) => subscribe(CHANNELS.meetingsChanged, callback),

  // — Kişiler ve şirketler —
  listPeople: () => listPeople(),
  getPersonProfile: (id) => getPersonProfile(requireId(id)),
  updatePerson: (id, patch) => updatePerson(requireId(id), patch ?? {}),
  deletePerson: (id) => deletePerson(requireId(id)),
  mergePeople: (keepId, dropId) => mergePeople(requireId(keepId), requireId(dropId)),
  listCompanies: () => listCompanies(),
  getCompanyProfile: (id) => getCompanyProfile(requireId(id)),
  createCompany: (name, domain) =>
    createCompany({ name: typeof name === 'string' ? name : '', domain: typeof domain === 'string' ? domain : null }),
  updateCompany: (id, patch) => updateCompany(requireId(id), patch ?? {}),
  assignSpeaker: async (meetingId, speaker, personId) => {
    if (typeof speaker !== 'string') throw new Error(t('Geçersiz konuşmacı.', 'Invalid speaker.'));
    await setSpeakerPerson(requireId(meetingId), speaker, typeof personId === 'string' && personId ? personId : null);
    notifyMeetingsChanged();
  },

  // — Arşiv — web'de klasör yerine ZIP iner.
  exportArchive: (companyId) => exportArchive(pageWebContents(), typeof companyId === 'string' && companyId ? companyId : undefined),
  onExportProgress: (callback) => subscribe(CHANNELS.exportProgress, callback),
  revealFolder: async () => {},

  // — Abonelik —
  getBillingStatus: () => getBillingStatus(),
  startBillingCheckout: async (input) => {
    // Ödeme sayfası aynı sekmede açılır (Otter/Stripe gibi); ödeme bitince sonuç
    // sayfasından uygulamaya dönülür.
    const url = await requestCheckoutUrl(input);
    if (!/^https:\/\//.test(url) && !url.startsWith('http://localhost')) throw new Error(t('Ödeme sayfası açılamadı.', 'Could not open the payment page.'));
    window.location.assign(url);
  },
  cancelBilling: () => cancelBillingSubscription(),
  refreshBilling: () => refreshBillingSubscription(),
  redeemCoupon: (code) => redeemCoupon(code),

  // — Toplantı botu —
  sendBot: (request: BotSendRequest) => sendBot(request),
  stopBot: async (meetingId) => {
    if (typeof meetingId !== 'string') throw new Error(t('Geçersiz toplantı.', 'Invalid meeting.'));
    return stopMeetingBot(meetingId);
  },
  getBotScheduleMap: () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return listScheduledEventIds(start.toISOString());
  },
  // Otomatik katılım web'de sunucuda çalışıyor (sekme kapalıyken de); tercih orada.
  getAutoJoinPrefs: () => autoJoinRequest(),
  setAutoJoinEnabled: (enabled) => autoJoinRequest({ enabled: enabled === true }),
  // Açılınca sunucu hemen bir tur atıyor; kapatılınca henüz girmemiş bot burada iptal edilir.
  setAutoJoinEvent: async (eventId, join) => {
    const prefs = await autoJoinRequest({ event: { eventId: requireId(eventId), join: join === true } });
    if (join !== true) await cancelBotForEvent(eventId);
    return prefs;
  },

  // — Yardım al —
  contactSupport: () => contactSupport(),
  submitFeatureRequest: (text) => submitFeatureRequest(text),
  reportAiContent: (input) => submitAiReport(input),
  saveFeedback: (text, email) => saveFeedback(text, email),

  // — Google Takvim —
  connectGoogleCalendar: () => connectGoogle(),
  disconnectGoogleCalendar: () => disconnectCalendar('google'),
  getGoogleCalendarStatus: () => getCalendarStatus(),
  getUpcomingCalendarEvents: () => getUpcomingCalendarEvents(),
  getTodayCalendarEvents: () => getTodayCalendarEvents(),
  listGoogleCalendars: () => listGoogleCalendars(),
  setGoogleCalendars: (ids) => setGoogleCalendars(ids),

  // — Outlook —
  connectOutlook: () => connectMicrosoft(),
  disconnectOutlook: () => disconnectCalendar('microsoft'),
  getOutlookStatus: () => getOutlookStatus(),

  // — Ayarlar —
  getRecordingPrefs: async () => {
    const { transcribeLanguage } = await getSettings();
    return { transcribeLanguage };
  },
  getSettings: async () => {
    await assertAdmin();
    return getSettings();
  },
  saveSettings: async (patch) => {
    await assertAdmin();
    return saveSettings(patch);
  },
  getDiagnostics: async (): Promise<DiagnosticsInfo> => {
    await assertAdmin();
    return {
      appVersion: WEB_CONFIG.version,
      electronVersion: '—',
      platform: navigator.userAgent,
      envSource: null,
      recordingsPath: t('Bu tarayıcı (IndexedDB)', 'This browser (IndexedDB)'),
      userDataPath: 'localStorage',
      keys: [
        { name: 'SUPABASE_URL', present: Boolean(WEB_CONFIG.supabaseUrl), required: true },
        { name: 'SUPABASE_ANON_KEY', present: Boolean(WEB_CONFIG.supabaseAnonKey), required: true },
        { name: 'API_URL', present: Boolean(WEB_CONFIG.apiUrl), required: true },
      ],
    };
  },

  // — Otomatik güncelleme — web her açılışta en son sürüm.
  getUpdateStatus: async () => updateStatus(),
  checkForUpdates: async () => updateStatus(),
  installUpdate: async () => {},
  onUpdateStatus: unsubscribeNoop,

  // — Özet e-postası —
  getSummaryEmailStatus: () => getSummaryEmailStatus(),
  saveSummaryEmailPrefs: (patch) => saveEmailSettings(patch),
  sendSummaryEmail: (meeting, force, meetingId) => sendSummaryEmail(meeting, force === true, meetingId),

  // — Kabuk —
  openExternal: async (url) => {
    if (typeof url !== 'string' || !openExternalSafely(url)) {
      throw new Error(t('Bu bağlantı açılamadı.', 'This link could not be opened.'));
    }
  },

  // — Yalnızca web —
  localAudioUrl: (filePath) => localAudioUrl(filePath),
  persistRecordingChunk: (recordingId, seq, chunk) => {
    activeRecordingId = recordingId;
    void putChunk({ recordingId, seq, blob: chunk, at: Date.now() }).catch((err) =>
      console.warn('[bridge] kayıt parçası tarayıcıya yazılamadı:', err)
    );
  },
  discardRecordingChunks: async (recordingId) => {
    if (activeRecordingId === recordingId) activeRecordingId = null;
    await deleteChunks(recordingId).catch(() => {});
  },
  takeRecoveredRecordings: async () => {
    if (recoveryTaken) return [];
    recoveryTaken = true;
    return recoverRecordings().catch((err) => {
      console.error('[bridge] yarım kayıtlar okunamadı:', err);
      return [];
    });
  },
  takeSignInError: () => takeSignInError(),
  emailAuth: {
    signIn: signInWithEmail,
    signUp: signUpWithEmail,
    verifySignup: verifySignupCode,
    resendSignupCode,
    requestPasswordReset,
    resetPassword,
  },
  takeCalendarConnectReturn: () => takeCalendarConnectReturn(),

  // — Yalnızca mobil (web'de tanımsız) —
  nativeMic,
  sharedAudio,
  storeBilling,
  pushNotifications,
  appleSignIn,

  // — Pencere (masaüstüne özgü) —
  minimizeWindow: noop,
  toggleMaximizeWindow: noop,
  closeWindow: noop,
  isWindowMaximized: async () => false,
  onWindowMaximizedChange: unsubscribeNoop,
};
